import React, { useState } from "react";
import { Button } from "@/dentlab/src/components/ui/button";
import { TabConfig, TabType } from "@/dentlab/src/types/tab-config.types";
import { useTabConfigStore } from "@/dentlab/src/store/TabConfig/tab-config.store";
import { TabCardEditor } from "./tab-card-editor.component";
import { Json } from "@/lib/supabase/supabaseTypesImport";
import { Plus, FileText, Clock, StickyNote, Truck, Sigma } from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/dentlab/src/components/ui/dropdown-menu";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	horizontalListSortingStrategy,
	arrayMove,
} from "@dnd-kit/sortable";
import { SortableItem } from "../../form-builder/components/ui/sortable-item";

interface TabConfigEditorProps {
	config: {
		id: string;
		name: string;
		tabs: Json;
	};
}

export const TabConfigEditor: React.FC<TabConfigEditorProps> = ({ config }) => {
	const rawTabs = (config.tabs as unknown as TabConfig[]) ?? [];
	const tabs = [...rawTabs].sort((a, b) => a.order - b.order);
	const updateConfiguration = useTabConfigStore(
		(state) => state.updateConfiguration
	);
	const [selectedTabId, setSelectedTabId] = useState<string | null>(
		tabs[0]?.id ?? null
	);

	const addEmptyTab = (type: TabType) => {
		let newTab: TabConfig;
		const newId = Date.now().toString();
		const newOrder = tabs.length;

		// Default color for all tab types
		const defaultColor = "#F9FAFB";

		// Default icons for different tab types
		const defaultIcons: Record<TabType, string | undefined> = {
			general: undefined,
			notes: "StickyNote",
			files: "Files",
			job_history: "History",
			deliveries: "Truck",
			total: "Sigma",
		};

		switch (type) {
			case "general":
				newTab = {
					id: newId,
					type: "general",
					order: newOrder,
					isDefaultActive: false,
					label: "Neuer Tab",
					fields: [],
					color: defaultColor,
					icon: defaultIcons[type],
				};
				break;
			case "notes":
				newTab = {
					id: newId,
					type: "notes",
					order: newOrder,
					isDefaultActive: false,
					color: defaultColor,
					icon: defaultIcons[type],
				};
				break;
			case "files":
				newTab = {
					id: newId,
					type: "files",
					order: newOrder,
					isDefaultActive: false,
					color: defaultColor,
					icon: defaultIcons[type],
				};
				break;
			case "job_history":
				newTab = {
					id: newId,
					type: "job_history",
					order: newOrder,
					isDefaultActive: false,
					color: defaultColor,
					icon: defaultIcons[type],
				};
				break;
			case "deliveries":
				newTab = {
					id: newId,
					type: "deliveries",
					order: newOrder,
					isDefaultActive: false,
					color: "#8b5cf6",
					icon: defaultIcons[type],
				};
				break;
			case "total":
				newTab = {
					id: newId,
					type: "total",
					order: newOrder,
					isDefaultActive: true,
					color: "#689689",
					icon: defaultIcons[type],
				};
				break;
		}

		updateConfiguration(config.id, [...tabs, newTab]);
		setSelectedTabId(newId);
	};

	const removeTab = (id: string) => {
		const tab = tabs.find((t) => t.id === id);
		if (tab?.type === "total") return;
		const newTabs = tabs
			.filter((t) => t.id !== id)
			.map((t, index) => ({ ...t, order: index }));
		updateConfiguration(config.id, newTabs);
		if (selectedTabId === id) {
			setSelectedTabId(newTabs.length > 0 ? newTabs[0].id : null);
		}
	};

	const updateTab = (id: string, updates: Partial<TabConfig>) => {
		const updatedTabs = tabs.map((t) =>
			t.id === id ? ({ ...t, ...updates } as TabConfig) : t
		);
		updateConfiguration(config.id, updatedTabs);
	};

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
				tolerance: 5,
				delay: 150,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = tabs.findIndex((tab) => tab.id === active.id);
			const newIndex = tabs.findIndex((tab) => tab.id === over.id);

			if (oldIndex !== -1 && newIndex !== -1) {
				const newTabs = arrayMove(tabs, oldIndex, newIndex).map(
					(tab, i) => ({ ...tab, order: i })
				);
				updateConfiguration(config.id, newTabs);
			}
		}
	};

	// Check if a non-general tab type already exists
	const hasTabType = (type: TabType): boolean => {
		return tabs.some((t) => t.type === type);
	};

	return (
		<div>
			{/* Tab Cards Container with horizontal scrolling */}
			<div className="overflow-x-auto pb-4">
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext
						items={tabs.map((tab) => tab.id)}
						strategy={horizontalListSortingStrategy}
					>
						<div className="flex gap-4 min-w-max">
							{tabs.map((tab) => (
								<SortableItem
									key={tab.id}
									id={tab.id}
									handleSelector=".drag-handle"
								>
									<TabCardEditor
										tab={tab}
										isSelected={selectedTabId === tab.id}
										onUpdate={(updates) =>
											updateTab(tab.id, updates)
										}
										onRemove={() => removeTab(tab.id)}
										onSelect={() =>
											setSelectedTabId(tab.id)
										}
									/>
								</SortableItem>
							))}

							{/* Add tab dropdown */}
							<div className="flex-shrink-0">
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											variant="outline"
											className="h-full min-h-[200px] w-32 flex flex-col items-center justify-center gap-2 border-2 border-dashed hover:border-gray-400"
										>
											<Plus className="h-6 w-6" />
											<span className="text-sm">
												Tab hinzufügen
											</span>
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent
										align="start"
										side="right"
									>
										<DropdownMenuItem
											onClick={() =>
												addEmptyTab("general")
											}
										>
											<FileText className="h-4 w-4 mr-2" />
											Allgemein
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() => addEmptyTab("notes")}
											disabled={hasTabType("notes")}
										>
											<StickyNote className="h-4 w-4 mr-2" />
											Notizen
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() => addEmptyTab("files")}
											disabled={hasTabType("files")}
										>
											<FileText className="h-4 w-4 mr-2" />
											Dateien
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() =>
												addEmptyTab("job_history")
											}
											disabled={hasTabType("job_history")}
										>
											<Clock className="h-4 w-4 mr-2" />
											Auftragshistorie
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() =>
												addEmptyTab("deliveries")
											}
											disabled={hasTabType("deliveries")}
										>
											<Truck className="h-4 w-4 mr-2" />
											Lieferungen
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() => addEmptyTab("total")}
											disabled={hasTabType("total")}
										>
											<Sigma className="h-4 w-4 mr-2" />
											Total
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</div>
					</SortableContext>
				</DndContext>
			</div>
		</div>
	);
};
